import React from 'react';

/**
 * Attribution footers for third-party data providers.
 *
 * Required to satisfy:
 *   • GreatSchools Attribution & Branding Requirements (2024-12) — mandates
 *     a visible "Data provided by GreatSchools.org © <year>. All rights
 *     reserved." notice + the official GreatSchools logo (min 95px wide
 *     desktop, 85px mobile, 15px clear space) on every page/view that
 *     renders GreatSchools data (school name, rating, grades, distance).
 *     All links to greatschools.org MUST carry `rel="nofollow"`.
 *   • RentCast API Terms — attribution is not strictly required but we
 *     disclose it for CCPA/GDPR hygiene and consistency.
 *
 * Usage: drop `<GreatSchoolsAttribution />` at the bottom of any page/
 * dialog that contains a school name or rating. Use `<DataProvidersFooter />`
 * for pages that mix GreatSchools + RentCast (listings page).
 */

const CURRENT_YEAR = new Date().getFullYear();

export function GreatSchoolsAttribution({ className = '', compact = false, showRatingScale = false }) {
  // GreatSchools 2024-12 spec, page 2:
  //   • Minimum logo width: 95px desktop / 85px mobile.
  //   • Minimum 15px clear space on all sides.
  //   • May be resized with proportional constraints only (no stretch/skew).
  // Tailwind sizes used: w-24 = 96px (mobile floor), w-28 = 112px (desktop).
  // Both exceed the published minimums with a safe margin. The `p-4`
  // on the surrounding wrapper + `gap-4` give 16px clear space, which
  // clears the 15px floor on every edge.
  const logoClasses = compact
    ? 'w-24 h-auto shrink-0'                            // 96px — mobile floor
    : 'w-24 sm:w-28 h-auto shrink-0';                   // 96 → 112px
  return (
    <div
      data-testid="greatschools-attribution"
      className={`flex items-center gap-4 p-4 text-[11px] text-brand-green/70 ${className}`}
    >
      <a
        href="https://www.greatschools.org"
        target="_blank"
        rel="nofollow noopener noreferrer"
        aria-label="GreatSchools.org — school ratings"
        className={`${logoClasses} block`}
      >
        {/* No background / border on the lockup — the spec forbids any
            co-branded framing. The logo PNG is pre-trimmed to the
            correct safe area. */}
        <img
          src="/brand/greatschools-logo.png"
          alt="GreatSchools"
          className="w-full h-auto object-contain"
        />
      </a>
      <div className="leading-snug">
        School data provided by{' '}
        <a
          href="https://www.greatschools.org"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="text-brand-green hover:underline"
        >
          GreatSchools.org
        </a>{' '}
        © {CURRENT_YEAR}. All rights reserved.{showRatingScale ? ' Ratings are on a 1–10 scale.' : ''}
      </div>
    </div>
  );
}

/** Combined footer for pages that show both AVM/sold data (RentCast) and
 * school data (GreatSchools). The RentCast line is a disclosure, not a
 * logo lock-up — RentCast's TOS doesn't require a logo.
 *
 * Pass `showRatingScale` when the page actually displays a numeric
 * GreatSchools rating anywhere. On lower subscription tiers that only
 * return school names + distance, we omit the "Ratings are on a 1–10
 * scale" sentence to avoid implying ratings are shown. */
export function DataProvidersFooter({ className = '', showRatingScale = false }) {
  return (
    <div
      data-testid="data-providers-footer"
      className={`mt-6 rounded-lg border border-brand-green/10 bg-brand-cream/40 ${className}`}
    >
      <GreatSchoolsAttribution showRatingScale={showRatingScale} />
      <div className="px-3 pb-2 text-[11px] text-brand-green/70 border-t border-brand-green/10 pt-2">
        Property-value estimates (AVM) and ZIP-level sold-price data provided by{' '}
        <a
          href="https://www.rentcast.io"
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="text-brand-green hover:underline"
        >
          RentCast
        </a>
        . Estimates are algorithmic and not a formal appraisal.
      </div>
    </div>
  );
}

/** Chip-sized inline link to a school's GreatSchools profile page.
 * Pass a null/empty `profileUrl` to render a plain span (we still show
 * the school name and rating, we just can't link). Per the GreatSchools
 * 2024-12 spec, the link's aria-label follows the required alt-text
 * pattern: "[School Name] GreatSchools Rating". */
export function SchoolChipLink({ profileUrl, name, children, title }) {
  if (!profileUrl) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-brand-green/10 text-brand-green truncate max-w-[14rem]"
        title={title}
        data-testid="chip-school"
      >
        {children}
      </span>
    );
  }
  return (
    <a
      href={profileUrl}
      target="_blank"
      rel="nofollow noopener noreferrer"
      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-brand-green/10 text-brand-green hover:bg-brand-green/20 truncate max-w-[14rem]"
      title={title}
      aria-label={`${name} GreatSchools Rating`}
      data-testid="chip-school"
    >
      {children}
    </a>
  );
}
